import { useEffect, useState } from "react";
import { fetchMe, type Me } from "./api.js";
import { AppHeader } from "./AppHeader.js";
import styles from "./App.module.css";
import { GuestHome } from "./GuestHome.js";
import { GuestSignIn } from "./GuestSignIn.js";
import { Home } from "./Home.js";
import { NewMap } from "./NewMap.js";
import { Workspace } from "./Workspace.js";

/** The map ID from a /maps/:id path, if this is one. */
function mapIdFromPath(path: string): string | null {
  const m = /^\/maps\/([A-Za-z0-9_-]{1,40})\/?$/.exec(path);
  return m ? (m[1] ?? null) : null;
}

/**
 * The invitation link (brief 8.2): /join/:id for one map, or /join to sign in without one. Returns
 * undefined when this is not a join path.
 */
function joinFromPath(path: string): { mapId?: string } | undefined {
  if (/^\/join\/?$/.test(path)) return {};
  const m = /^\/join\/([A-Za-z0-9_-]{1,40})\/?$/.exec(path);
  return m?.[1] ? { mapId: m[1] } : undefined;
}

const SIGN_IN_MESSAGES: Record<string, string> = {
  denied:
    "Your Salesforce user does not have access to the Value Map Builder. Ask a Salesforce admin to assign the Value Map User permission set.",
  failed: "Sign-in did not complete. Please try again.",
  unavailable: "Sign-in is unavailable right now. Please try again shortly.",
};

type State =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "signedIn"; me: Me }
  | { status: "error" };

export function App() {
  const [state, setState] = useState<State>({ status: "loading" });
  const signInProblem = new URLSearchParams(window.location.search).get("signin");
  const problem = signInProblem ? SIGN_IN_MESSAGES[signInProblem] : undefined;

  useEffect(() => {
    const ctl = new AbortController();
    fetchMe(ctl.signal).then(
      (me) => setState(me ? { status: "signedIn", me } : { status: "signedOut" }),
      (err: unknown) => {
        if ((err as Error).name !== "AbortError") setState({ status: "error" });
      },
    );
    return () => ctl.abort();
  }, []);

  // Keep the page (and the Account and Opportunity on a /new link) through sign-in.
  const returnTo =
    window.location.pathname === "/"
      ? ""
      : `?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;

  const path = window.location.pathname;
  const join = joinFromPath(path);
  const mapId = mapIdFromPath(path) ?? join?.mapId ?? null;

  // A map fills the page with its own toolbar; every other page shares the plain header.
  if (state.status === "signedIn" && mapId)
    return <Workspace key={mapId} id={mapId} me={state.me} />;

  // Client participants sign in with an emailed code, from their invitation link.
  if (state.status === "signedOut" && join) {
    return (
      <>
        <AppHeader />
        <GuestSignIn mapId={join.mapId} />
      </>
    );
  }

  const signedInPage =
    state.status === "signedIn" &&
    (state.me.type === "guest" ? <GuestHome /> : path === "/new" ? <NewMap /> : <Home />);

  return (
    <>
      <AppHeader me={state.status === "signedIn" ? state.me : undefined} />

      {signedInPage}

      {state.status !== "signedIn" && (
        <main className={styles.main}>
          <div className={styles.card} aria-live="polite">
            {state.status === "loading" && <p>Loading…</p>}

            {state.status === "error" && (
              <>
                <h1>Something went wrong</h1>
                <p>The Value Map Builder could not be reached. Please refresh the page.</p>
              </>
            )}

            {state.status === "signedOut" && (
              <>
                <div className={styles.eyebrow}>SalesFix staff</div>
                <h1>Value Map Builder</h1>
                {problem && (
                  <div className={styles.alert} role="alert">
                    {problem}
                  </div>
                )}
                <p>Sign in with your SalesFix Salesforce login.</p>
                <a
                  className={`${styles.btn} ${styles.primary}`}
                  href={`/auth/salesforce/login${returnTo}`}
                >
                  Sign in with Salesforce
                </a>
                <p className={styles.guestLink}>
                  Invited to a value map by SalesFix?{" "}
                  <a href={mapId ? `/join/${encodeURIComponent(mapId)}` : "/join"}>
                    Sign in with your email
                  </a>
                </p>
              </>
            )}
          </div>
        </main>
      )}
    </>
  );
}
